import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import AddCircleIcon from '@atlaskit/icon/glyph/add-circle';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { OptionItemObject } from '../../../../../../src/shared_models/interfaces';
import {
    ADD_OPTION_CLICKED,
    CHANGE_SORT_DIRECTION_TOOLTIP_TEXT,
    MAX_NESTING_LEVEL,
    NO_OPTION_INDEX,
    REORDER_OPTIONS,
    SORT_OPTIONS,
} from '../../../utils/constants';
import SortDownIcon from '../../common/icons/SortDownIcon';
import SortUpIcon from '../../common/icons/SortUpIcon';

import { OptionsAdderProperties } from './interfaces';
import {
    ActionItem,
    ActionsBar,
    AddOptionButton,
    AddOptionButtonItemsWrapper,
    AddOptionInfoFrame,
    AddOptionText,
    RightSide,
} from './OptionsAdderStyled';
import OptionsList from './OptionsList';
import { isAddOptionButtonClickable, reorderDraggableElements } from './utils';

const OptionsAdder: React.FC<OptionsAdderProperties> = ({
    onChange,
    options,
    onConfigureOptionsClick,
    optionsWithUpdatedName,
    currentNestingLevel,
    setIsAddBulkOptionsModalOpened,
    setNestingLevelChanged,
    nestingLevelChanged,
    handleDeleteOptionClick,
    locale,
}) => {
    const [sortingTriggered, setSortingTriggered] = useState(false);
    const [isSortedAsc, setIsSortedAsc] = useState(false);
    const handleOptionsSorting = () => {
        if (options.length === 0) {
            return;
        }

        if (sortingTriggered) {
            setIsSortedAsc(!isSortedAsc);
        } else {
            setIsSortedAsc(true);
        }

        onChange({ options, isSortedAsc }, SORT_OPTIONS, NO_OPTION_INDEX, optionsWithUpdatedName);
        setSortingTriggered(true);
    };

    const getSortIconWithTooltip = (isAsc = true): JSX.Element => (
        <Tooltip
            content={CHANGE_SORT_DIRECTION_TOOLTIP_TEXT}
            position={'right'}
        >
            {isAsc ? <SortUpIcon /> : <SortDownIcon />}
        </Tooltip>
    );

    const renderSortIconWithTooltip = (): JSX.Element => {
        if (sortingTriggered) {
            return getSortIconWithTooltip(isSortedAsc);
        }

        return getSortIconWithTooltip(false);
    };

    const resetSortingValuesToDefaults = (): void => {
        setSortingTriggered(false);
        setIsSortedAsc(false);
    };

    const showAddOptionInfoText = (nestingLevel = 1) =>
        nestingLevel === MAX_NESTING_LEVEL ? (
            <p>Add first option in Level {nestingLevel}</p>
        ) : (
            <p>
                Add first option in Level {nestingLevel} to configure <br /> next levels
            </p>
        );

    const isTryingToDragEmptyOption = (options: OptionItemObject[], draggedOptionIndex: number): boolean => {
        const draggedOption = options.find((option: OptionItemObject, index: number) => index === draggedOptionIndex);

        return !draggedOption!.value;
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination || isTryingToDragEmptyOption(options, result.source.index)) {
            return;
        }

        const optionsNewOrder = reorderDraggableElements(options, result.source.index, result.destination.index);

        onChange(optionsNewOrder, REORDER_OPTIONS, NO_OPTION_INDEX, optionsWithUpdatedName);
    };

    useEffect(() => {
        if (nestingLevelChanged) {
            resetSortingValuesToDefaults();
            setNestingLevelChanged!(false);
        }
    }, [nestingLevelChanged]);

    return (
        <>
            <ActionsBar>
                <div>
                    <ActionItem onClick={handleOptionsSorting}>{renderSortIconWithTooltip()}</ActionItem>
                </div>
                <RightSide>
                    <ActionItem
                        className='text-option'
                        onClick={() => setIsAddBulkOptionsModalOpened!(true)}
                    >
                        Add options in bulk
                    </ActionItem>
                </RightSide>
            </ActionsBar>
            {options.length > 0 && (
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId='droppable'>
                        {provided => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                            >
                                <OptionsList
                                    optionsWithUpdatedName={optionsWithUpdatedName}
                                    options={options}
                                    onChange={onChange}
                                    onConfigureOptionsClick={onConfigureOptionsClick}
                                    handleDeleteOptionClick={handleDeleteOptionClick}
                                    nestingLevelChanged={nestingLevelChanged}
                                    currentNestingLevel={currentNestingLevel}
                                    locale={locale}
                                />
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            )}
            {options.length === 0 && <AddOptionInfoFrame>{showAddOptionInfoText(currentNestingLevel)}</AddOptionInfoFrame>}
            <AddOptionButton
                onClick={() =>
                    isAddOptionButtonClickable(options) &&
                    onChange('', ADD_OPTION_CLICKED, NO_OPTION_INDEX, optionsWithUpdatedName)
                }
            >
                <AddOptionButtonItemsWrapper>
                    <AddCircleIcon
                        label='Add option'
                        primaryColor={token('color.text.accent.blue')}
                        secondaryColor={token('color.icon.inverse')}
                        size='medium'
                    />
                    <AddOptionText>Add option</AddOptionText>
                </AddOptionButtonItemsWrapper>
            </AddOptionButton>
        </>
    );
};

export default OptionsAdder;
